import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import type { ItemsErrors } from "../../helpers/validation";
import type {
  HomeFieldValue,
  HomeItemsConfig,
  HomeSectionItem,
} from "../../type/homePage";
import SectionFields from "./SectionFields";

interface ItemsEditorProps {
  config: HomeItemsConfig;
  items: HomeSectionItem[];
  errors: ItemsErrors;
  generalError?: string;
  onChange: (items: HomeSectionItem[]) => void;
  onAdd: () => void;
  /** Dipanggil tanpa argumen saat struktur berubah (tambah/hapus/urut ulang), atau (index, key) saat satu field diedit. */
  onClearError: (index?: number, key?: string) => void;
}

export default function ItemsEditor({
  config,
  items,
  errors,
  generalError,
  onChange,
  onAdd,
  onClearError,
}: ItemsEditorProps) {
  const { label, itemLabel, titleKey, fields, min, max } = config;

  const reachedMax = max !== undefined && items.length >= max;
  const canRemove = items.length > (min ?? 0);

  const handleFieldChange = (
    index: number,
    key: string,
    value: HomeFieldValue
  ) => {
    onChange(
      items.map((item, i) =>
        i === index
          ? { ...item, values: { ...item.values, [key]: value } }
          : item
      )
    );
    onClearError(index, key);
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;

    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];

    onChange(next);
    onClearError();
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
    onClearError();
  };

  const getItemTitle = (item: HomeSectionItem, index: number) => {
    const raw = item.values[titleKey];
    const trimmed = typeof raw === "string" ? raw.trim() : "";
    // Judul ringkas: isian panjang (mis. poin Visi/Misi) dipotong.
    const text = trimmed.length > 60 ? `${trimmed.slice(0, 60)}…` : trimmed;

    return text
      ? `${itemLabel} ${index + 1} - ${text}`
      : `${itemLabel} ${index + 1}`;
  };

  return (
    <div id="home-items-section">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{label}</h2>

          <p className="text-sm text-gray-500">
            {items.length} {itemLabel.toLowerCase()}
            {max !== undefined ? ` (max ${max})` : ""} - urutan di sini sama
            dengan urutan tampil di website.
          </p>
        </div>

        <button
          type="button"
          onClick={onAdd}
          disabled={reachedMax}
          className="flex h-11 items-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
          <Plus size={18} />
          Add {itemLabel}
        </button>
      </div>

      {generalError && (
        <p className="mb-3 text-sm text-red-500">{generalError}</p>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 py-10 text-center text-gray-400">
          No {itemLabel.toLowerCase()} yet
        </div>
      ) : (
        <div className="space-y-5">
          {items.map((item, index) => (
            <div
              key={item.uid}
              className="rounded-2xl border border-gray-200 p-6">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  {getItemTitle(item, index)}
                </h3>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleMove(index, -1)}
                    disabled={index === 0}
                    aria-label="Move up"
                    className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40">
                    <ChevronUp size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleMove(index, 1)}
                    disabled={index === items.length - 1}
                    aria-label="Move down"
                    className="rounded-lg bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40">
                    <ChevronDown size={18} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={!canRemove}
                    aria-label={`Remove ${itemLabel}`}
                    title={
                      canRemove
                        ? `Remove ${itemLabel}`
                        : `At least ${min} ${itemLabel.toLowerCase()} is required`
                    }
                    className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-40">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <SectionFields
                fields={fields}
                values={item.values}
                errors={errors[index]}
                idPrefix={`item-${index}-`}
                compact
                onChange={(key, value) => handleFieldChange(index, key, value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
